import { useEffect, useMemo, useState } from 'react';
import { Map } from 'immutable';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    getBlockLists,
    prioritiseNumbers,
    removeOwnPriorityEntries,
} from 'flow/actions/call-routing-actions';
import {
    selectCallRoutingsLoaded,
    selectPriorityCallRoutings,
} from 'flow/selectors/call-routing-selectors';
import { getOwnCountryCode } from 'flow/selectors/selectors';
import {
    findPriorityEntriesForNumbers,
    getContactNumbers,
} from 'flow/utils/priority-number-utils';
import { getContactNameOrCompanyName } from 'flow/utils/contact-utils';
import { TypedMap } from 'flow/types/TypedMap';
import { Contact, ContactFull } from 'flow/types/Contact';

/**
 * State and handler for the "Prioritise calls" switch of a contact: whether
 * any of the contact's numbers is let through as a priority call, whether only
 * an admin set that (so the user cannot undo it), and the texts to show.
 * Loads the call routings when they are not loaded yet. Not visible when the
 * contact has no numbers or the call routings could not be loaded.
 */
const usePriorityCalls = (
    typedContact: TypedMap<Contact> | TypedMap<ContactFull>,
) => {
    const contact = typedContact as unknown as Map<string, unknown>;
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const loaded = useAppSelector(selectCallRoutingsLoaded);
    const priorityEntries = useAppSelector(selectPriorityCallRoutings);
    const countryCode = useAppSelector(getOwnCountryCode);
    const [pending, setPending] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        if (loaded) {
            return undefined;
        }

        let cancelled = false;
        dispatch(getBlockLists()).then((succeeded: boolean) => {
            if (!succeeded && !cancelled) {
                setLoadFailed(true);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [dispatch, loaded]);

    const numbers = useMemo(
        () => getContactNumbers(contact, countryCode),
        [contact, countryCode],
    );
    const matchingEntries = useMemo(
        () =>
            findPriorityEntriesForNumbers(
                priorityEntries,
                numbers,
                countryCode,
            ),
        [priorityEntries, numbers, countryCode],
    );

    const visible = numbers.length > 0 && !loadFailed;
    const prioritised = matchingEntries.size > 0;
    const setByAdmin =
        prioritised &&
        !matchingEntries.some((entry) => Boolean(entry.get('editByUser')));
    const name =
        contact.get('firstName') || getContactNameOrCompanyName(contact);
    const title = i18next.t('ContactCard.PrioritiseCalls.Title');
    let description = i18next.t('ContactCard.PrioritiseCalls.HintNoName');
    if (setByAdmin) {
        description = i18next.t('ContactCard.PrioritiseCalls.SetByAdmin');
    } else if (name) {
        description = i18next.t('ContactCard.PrioritiseCalls.Hint', { name });
    }

    const onChange = async (checked: boolean) => {
        setPending(true);
        if (checked) {
            await dispatch(prioritiseNumbers(numbers));
        } else {
            await dispatch(removeOwnPriorityEntries(matchingEntries));
        }
        setPending(false);
    };

    return {
        visible,
        prioritised,
        setByAdmin,
        loading: !loaded || pending,
        title,
        description,
        onChange,
    };
};

export default usePriorityCalls;
